import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    Switch,
    Tab,
    Tabs,
    TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { ORTHOIMAGE_RGB_TGDT_UUID, PAGINATION } from "../../constants";
import {
    PARCELLAIRE_BUTTON_STYLE,
    PARCELLAIRE_TAB_BOX_STYLE,
} from "./constants";
import { formatDateTime, offset } from "../../utils/formatTime";
import { useCompanies, useSnackbar } from "../../hooks";

import { BACKEND_ROUTES } from "../../backendRoutes";
import DoneIcon from "@mui/icons-material/Done";
import { FetchErrorAlert } from "../../components/FetchErrorAlert";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { MODEL_PROPTYPES } from "../../models";
import { PARCELLAIRE_REDUCER_ACTIONS } from "./parcellaireReducer";
import PropTypes from "prop-types";
import { TabPanel } from "../../components";
import useSWR from "swr";
import { useState } from "react";

ParcellaireOrthoForm.propTypes = {
    setIsOpen: PropTypes.func.isRequired,
    parcellaireDispatch: PropTypes.func.isRequired,
    setGeoJsonKey: PropTypes.func.isRequired,
    initialCompany: PropTypes.shape(MODEL_PROPTYPES.Company),
    initialContract: PropTypes.shape(MODEL_PROPTYPES.Contract),
    initialSite: PropTypes.shape(MODEL_PROPTYPES.Site),
    initialMission: PropTypes.shape(MODEL_PROPTYPES.Mission),
    orthoimageCompany: PropTypes.shape(MODEL_PROPTYPES.Company),
    orthoimageContract: PropTypes.shape(MODEL_PROPTYPES.Contract),
    orthoimageSite: PropTypes.shape(MODEL_PROPTYPES.Site),
    orthoimageMission: PropTypes.shape(MODEL_PROPTYPES.Mission),
    fastOrthoimage: PropTypes.bool,
    setSearchParams: PropTypes.func.isRequired,
};

export function ParcellaireOrthoForm({
    setIsOpen,
    parcellaireDispatch,
    setGeoJsonKey,
    initialCompany,
    initialContract,
    initialSite,
    initialMission,
    orthoimageCompany,
    orthoimageContract,
    orthoimageSite,
    orthoimageMission,
    fastOrthoimage = true,
    setSearchParams,
}) {
    const { openSnackbar } = useSnackbar();
    const [tabValue, setTabValue] = useState(0);

    const handleChange = (_, newValue) => {
        setTabValue(newValue);
    };

    const { control, watch, setValue, handleSubmit, reset } = useForm({
        defaultValues: {
            metadata: null,
            company: orthoimageCompany || initialCompany || null,
            contract: orthoimageContract || initialContract || null,
            site: orthoimageSite || initialSite || null,
            mission: orthoimageMission || initialMission || null,
            fastOrtho: fastOrthoimage,
        },
    });

    const [
        metadataWatch,
        companyWatch,
        contractWatch,
        siteWatch,
        missionWatch,
        fastOrthoWatch,
    ] = watch([
        "metadata",
        "company",
        "contract",
        "site",
        "mission",
        "fastOrtho",
    ]);

    const {
        data: companies,
        error: companiesFetchError,
        isLoading: areCompaniesLoading,
    } = useCompanies();

    const { data: contracts, error: contractsFetchError } = useSWR(
        companyWatch
            ? `${BACKEND_ROUTES.CONTRACT}?companyUuid=${companyWatch.uuid}&sort=name&limit=${PAGINATION.CONTRACT.LIMIT.MAX}`
            : null
    );

    const { data: sites, error: sitesFetchError } = useSWR(
        contractWatch
            ? `${BACKEND_ROUTES.SITE}?contractUuid=${contractWatch.uuid}&sort=name&limit=${PAGINATION.SITE.LIMIT.MAX}`
            : null
    );

    const { data: missions, error: missionsFetchError } = useSWR(
        siteWatch
            ? `${BACKEND_ROUTES.MISSION}?siteUuid=${siteWatch.uuid}&parentInfo=true&pipelineInstancesTGDTUuid=${ORTHOIMAGE_RGB_TGDT_UUID}&sort=date&limit=${PAGINATION.GENERIC.LIMIT.MAX}`
            : null
    );

    const onSubmit = async (payload) => {
        try {
            if (tabValue === 1) {
                parcellaireDispatch({
                    type: PARCELLAIRE_REDUCER_ACTIONS.METADATA,
                    metadata: {
                        ...payload.metadata,
                        nameSpace: "cloverfield",
                        source: "import",
                    },
                });
                setSearchParams(() => {
                    const newSearchParams = new URLSearchParams();
                    newSearchParams.set("fastOrthoimage", false);
                    return newSearchParams;
                });
            } else {
                setSearchParams((prevState) => {
                    const newSearchParams = new URLSearchParams(prevState);
                    newSearchParams.set("missionUuid", payload.mission.uuid);
                    newSearchParams.set("siteUuid", payload.site.uuid);
                    newSearchParams.set("fastOrthoimage", payload.fastOrtho);
                    return newSearchParams;
                });
            }
            setGeoJsonKey(new Date().getTime());
            setIsOpen(false);
        } catch (error) {
            openSnackbar(
                "Something went wrong. Please try again later.",
                "error"
            );
        }
    };

    const mergedFetchError =
        missionsFetchError ??
        sitesFetchError ??
        contractsFetchError ??
        companiesFetchError;
    if (mergedFetchError) return <FetchErrorAlert error={mergedFetchError} />;
    if (!companies)
        if (areCompaniesLoading) return <CircularProgress />;
        else return null;

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <DialogTitle>Orthoimage source</DialogTitle>
            <DialogContent>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                    <Tabs
                        value={tabValue}
                        onChange={handleChange}
                        sx={{ flexGrow: 1 }}
                    >
                        <Tab label="Select mission" />
                        <Tab label="Import JSON" />
                    </Tabs>
                </Box>
                <TabPanel
                    value={tabValue}
                    index={0}
                    boxStyle={PARCELLAIRE_TAB_BOX_STYLE}
                >
                    <Grid container spacing={3}>
                        <Grid size={12}>
                            <Controller
                                control={control}
                                name="company"
                                render={({
                                    field: {
                                        ref,
                                        onChange,
                                        name,
                                        ...fieldProps
                                    },
                                }) => (
                                    <Autocomplete
                                        {...fieldProps}
                                        id="company"
                                        options={companies.rows}
                                        isOptionEqualToValue={(option, value) =>
                                            option.uuid === value.uuid
                                        }
                                        getOptionLabel={(option) => option.name}
                                        onChange={(_, value) => {
                                            onChange(value);
                                            reset((formValues) => ({
                                                ...formValues,
                                                contract: null,
                                                site: null,
                                                mission: null,
                                            }));
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                inputRef={ref}
                                                name={name}
                                                label="Company"
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={12}>
                            <Controller
                                control={control}
                                name="contract"
                                render={({
                                    field: {
                                        ref,
                                        onChange,
                                        name,
                                        ...fieldProps
                                    },
                                }) => (
                                    <Autocomplete
                                        {...fieldProps}
                                        id="contract"
                                        disabled={!companyWatch || !contracts}
                                        options={contracts?.rows ?? []}
                                        isOptionEqualToValue={(option, value) =>
                                            option.uuid === value.uuid
                                        }
                                        getOptionLabel={(option) => option.name}
                                        onChange={(_, value) => {
                                            onChange(value);
                                            reset((formValues) => ({
                                                ...formValues,
                                                site: null,
                                                mission: null,
                                            }));
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                inputRef={ref}
                                                name={name}
                                                label="Campaign"
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={12}>
                            <Controller
                                control={control}
                                name="site"
                                render={({
                                    field: {
                                        ref,
                                        onChange,
                                        name,
                                        ...fieldProps
                                    },
                                }) => (
                                    <Autocomplete
                                        {...fieldProps}
                                        id="site"
                                        disabled={!contractWatch || !sites}
                                        options={sites?.rows ?? []}
                                        isOptionEqualToValue={(option, value) =>
                                            option.uuid === value.uuid
                                        }
                                        getOptionLabel={(option) => option.name}
                                        onChange={(_, value) => {
                                            onChange(value);
                                            reset((formValues) => ({
                                                ...formValues,
                                                mission: null,
                                            }));
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                inputRef={ref}
                                                name={name}
                                                label="Site"
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={12}>
                            <Controller
                                control={control}
                                name="mission"
                                render={({
                                    field: {
                                        ref,
                                        onChange,
                                        name,
                                        ...fieldProps
                                    },
                                }) => (
                                    <Autocomplete
                                        {...fieldProps}
                                        id="mission"
                                        disabled={!siteWatch || !missions}
                                        options={missions?.rows ?? []}
                                        isOptionEqualToValue={(option, value) =>
                                            option.uuid === value.uuid
                                        }
                                        getOptionLabel={(option) =>
                                            formatDateTime(
                                                offset(
                                                    new Date(option.date),
                                                    true
                                                )
                                            )
                                        }
                                        onChange={(_, value) => {
                                            onChange(value);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                inputRef={ref}
                                                name={name}
                                                label="Mission"
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={12}>
                            <Controller
                                control={control}
                                name="fastOrtho"
                                render={({
                                    field: {
                                        ref,
                                        onChange,
                                        name,
                                        ...fieldProps
                                    },
                                }) => (
                                    <FormControlLabel
                                        {...fieldProps}
                                        id="fastOrtho"
                                        name="fastOrtho"
                                        control={<Switch />}
                                        label="Use fast orthoimages"
                                        labelPlacement="end"
                                        checked={fastOrthoWatch}
                                        onChange={onChange}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </TabPanel>
                <TabPanel
                    value={tabValue}
                    index={1}
                    boxStyle={PARCELLAIRE_TAB_BOX_STYLE}
                >
                    <Grid container spacing={3}>
                        <Grid
                            container
                            spacing={2}
                            alignItems="center"
                            size={12}
                        >
                            <Grid size="auto">
                                <Button
                                    variant="contained"
                                    component="label"
                                    sx={PARCELLAIRE_BUTTON_STYLE}
                                    startIcon={<FileUploadIcon />}
                                >
                                    session-metadata.json
                                    <input
                                        hidden
                                        type="file"
                                        accept=".json"
                                        onChange={(event) => {
                                            if (event.target.files[0]) {
                                                const reader = new FileReader();

                                                reader.onload = function (e) {
                                                    const jsonData = JSON.parse(
                                                        e.target.result
                                                    );

                                                    if (
                                                        jsonData?.site?.id &&
                                                        jsonData?.date
                                                    ) {
                                                        setValue(
                                                            "metadata",
                                                            jsonData
                                                        );
                                                    } else
                                                        openSnackbar(
                                                            "This session-metadata.json file is not valid.",
                                                            "error"
                                                        );
                                                };

                                                reader.readAsText(
                                                    event.target.files[0]
                                                );
                                            }
                                        }}
                                    />
                                </Button>
                            </Grid>
                            <Grid size="auto">
                                {metadataWatch && <DoneIcon color="success" />}
                            </Grid>
                        </Grid>
                    </Grid>
                </TabPanel>
            </DialogContent>
            <DialogActions>
                <Button type="button" onClick={() => setIsOpen(false)}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    type="submit"
                    disabled={
                        (tabValue === 0 && !missionWatch) ||
                        (tabValue === 1 && !metadataWatch)
                    }
                >
                    Load orthoimage
                </Button>
            </DialogActions>
        </form>
    );
}
